import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  ArrowRight, ArrowLeft, Briefcase, MapPin, Clock, DollarSign,
  Search, SlidersHorizontal, X, Sparkles, ExternalLink, Building2,
  Zap, ChevronDown, ChevronUp, AlertCircle, Loader2, Filter,
  Users, BarChart3, TrendingUp, Star, CheckCircle2, Shield, Rocket,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ════════════════════════════════════════════════════════════════════
   STYLES  (jp- prefix — zero collision with lp- landing page styles)
════════════════════════════════════════════════════════════════════ */
const JP_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --jp-cream:    #FAF8F5;
    --jp-ink:      #1A1814;
    --jp-ink-80:   rgba(26,24,20,0.80);
    --jp-ink-50:   rgba(26,24,20,0.50);
    --jp-ink-15:   rgba(26,24,20,0.15);
    --jp-ink-07:   rgba(26,24,20,0.07);
    --jp-gold:     #C8973A;
    --jp-gold-lt:  #E8BF72;
    --jp-gold-bg:  rgba(200,151,58,0.09);
    --jp-gold-bdr: rgba(200,151,58,0.25);
    --jp-sage:     #4A5E52;
    --jp-card-bg:  #FFFFFF;
    --jp-radius:   16px;
    --jp-radius-sm:10px;
    --jp-shadow:   0 4px 32px rgba(26,24,20,0.08);
    --jp-shadow-lg:0 20px 60px rgba(26,24,20,0.14);
    --jp-red:      #C0392B;
    --jp-red-bg:   rgba(192,57,43,0.08);
    --jp-green:    #27AE60;
    --jp-green-bg: rgba(39,174,96,0.08);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .jp-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--jp-cream);
    color: var(--jp-ink);
    min-height: 100vh;
    overflow-x: hidden;
    /* ✅ FIX 1: Push content below the fixed navbar */
    padding-top: 68px;
  }

  .jp-root::before {
    content: '';
    position: fixed; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.028'/%3E%3C/svg%3E");
    pointer-events: none; z-index: 0;
  }

  /* ── Nav ── */
  .jp-nav {
    /* ✅ FIX 1: fixed so it stays visible on scroll */
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 100;
    padding: 0 clamp(1.25rem, 5vw, 3rem);
    height: 68px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(250,248,245,0.97);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--jp-ink-15);
    box-shadow: 0 1px 0 var(--jp-ink-07), 0 2px 12px rgba(26,24,20,0.06);
  }
  .jp-nav-brand {
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; text-decoration: none;
    flex-shrink: 0;
  }
  .jp-nav-logo-img {
    height: 32px;
    width: auto;
    object-fit: contain;
    display: block;
  }
  .jp-nav-logo-text {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 1.35rem; font-weight: 900; color: var(--jp-ink); letter-spacing: -0.5px;
  }
  .jp-nav-logo-dot {
    width: 6px; height: 6px; border-radius: 50%; background: var(--jp-gold);
    display: inline-block; margin-left: 1px; position: relative; top: -3px;
  }
  .jp-nav-pill {
    background: var(--jp-gold-bg); border: 1px solid var(--jp-gold-bdr);
    color: #9A6F1E; font-family: 'DM Mono', monospace;
    font-size: 0.65rem; font-weight: 500; letter-spacing: 0.1em;
    padding: 4px 10px; border-radius: 50px;
    flex-shrink: 0;
  }
  .jp-nav-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .jp-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 20px; font-family: 'DM Sans', sans-serif;
    font-size: 0.85rem; font-weight: 600; border-radius: 50px;
    cursor: pointer; transition: all 0.2s ease; border: none;
    text-decoration: none; line-height: 1; white-space: nowrap;
  }
  .jp-btn-primary { background: var(--jp-ink); color: var(--jp-cream); }
  .jp-btn-primary:hover {
    background: var(--jp-gold); color: var(--jp-ink);
    transform: translateY(-2px); box-shadow: 0 8px 28px rgba(200,151,58,0.35);
  }
  .jp-btn-ghost {
    background: transparent; color: var(--jp-ink-80);
    border: 1px solid var(--jp-ink-15);
  }
  .jp-btn-ghost:hover { background: var(--jp-ink-07); border-color: var(--jp-ink-50); }

  @media (max-width: 400px) {
    .jp-btn-ghost .jp-btn-home-label { display: none; }
    .jp-btn-ghost { padding: 9px 12px; }
    .jp-btn-primary { padding: 9px 14px; font-size: 0.78rem; }
    .jp-nav-pill { display: none; }
  }

  /* ── Hero ── */
  .jp-hero {
    position: relative; z-index: 1;
    padding: clamp(3rem, 6vw, 5rem) clamp(1.25rem, 5vw, 3rem) 0;
    text-align: center; overflow: hidden;
  }
  .jp-hero-bg {
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% 0%, rgba(200,151,58,0.07) 0%, transparent 65%);
    pointer-events: none;
  }
  .jp-hero-eyebrow {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 6px 16px; border-radius: 50px;
    background: var(--jp-gold-bg); border: 1px solid var(--jp-gold-bdr);
    color: #9A6F1E; font-size: 0.78rem; font-weight: 600;
    letter-spacing: 0.03em; margin-bottom: 1.5rem;
    animation: jp-fadeUp 0.6s ease both;
  }
  .jp-hero-h1 {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(2.2rem, 5.5vw, 3.8rem); font-weight: 900;
    line-height: 1.08; letter-spacing: -1px; margin-bottom: 1rem;
    color: var(--jp-ink); position: relative; z-index: 1;
    animation: jp-fadeUp 0.65s ease 0.08s both;
  }
  .jp-hero-h1 em { font-style: italic; color: var(--jp-gold); }
  .jp-hero-sub {
    font-size: clamp(0.95rem, 2vw, 1.1rem); color: var(--jp-ink-50);
    max-width: 500px; margin: 0 auto 2.5rem; line-height: 1.7;
    animation: jp-fadeUp 0.65s ease 0.18s both; position: relative; z-index: 1;
  }

  /* ── Search bar ── */
  .jp-search-bar {
    max-width: 820px; margin: 0 auto;
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: 18px; padding: 10px 10px 10px 20px;
    display: flex; align-items: center; gap: 10px;
    box-shadow: var(--jp-shadow-lg);
    animation: jp-fadeUp 0.65s ease 0.28s both;
    position: relative; z-index: 2;
  }
  .jp-search-bar:focus-within {
    border-color: var(--jp-gold-bdr);
    box-shadow: 0 0 0 3px var(--jp-gold-bg), var(--jp-shadow-lg);
  }
  .jp-search-icon { color: var(--jp-ink-50); flex-shrink: 0; }
  .jp-search-input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: 'DM Sans', sans-serif; font-size: 0.95rem;
    color: var(--jp-ink); min-width: 0;
  }
  .jp-search-input::placeholder { color: var(--jp-ink-50); }
  .jp-search-divider { width: 1px; height: 28px; background: var(--jp-ink-15); flex-shrink: 0; }
  .jp-search-location {
    display: flex; align-items: center; gap: 6px;
    font-size: 0.88rem; color: var(--jp-ink-50);
    white-space: nowrap; flex-shrink: 0;
  }
  .jp-search-location input {
    border: none; outline: none; background: transparent;
    font-family: 'DM Sans', sans-serif; font-size: 0.88rem;
    color: var(--jp-ink); width: 120px;
  }
  .jp-search-location input::placeholder { color: var(--jp-ink-50); }
  .jp-search-btn {
    background: var(--jp-ink); color: var(--jp-cream);
    border: none; border-radius: 12px; padding: 12px 24px;
    font-family: 'DM Sans', sans-serif; font-size: 0.9rem; font-weight: 600;
    cursor: pointer; display: flex; align-items: center; gap: 7px;
    transition: all 0.2s; flex-shrink: 0; white-space: nowrap;
  }
  .jp-search-btn:hover {
    background: var(--jp-gold); color: var(--jp-ink);
    transform: translateY(-1px); box-shadow: 0 6px 20px rgba(200,151,58,0.35);
  }

  /* ── Stats strip ── */
  .jp-stats-strip {
    display: flex; align-items: center; justify-content: center;
    gap: clamp(1.5rem, 4vw, 3.5rem); padding: 1.75rem 1.25rem;
    position: relative; z-index: 1;
    animation: jp-fadeUp 0.65s ease 0.4s both;
    flex-wrap: wrap;
  }
  .jp-stat-item { text-align: center; }
  .jp-stat-val {
    font-family: 'Playfair Display', serif; font-size: 1.5rem;
    font-weight: 900; color: var(--jp-ink); line-height: 1;
  }
  .jp-stat-lbl {
    font-size: 0.7rem; font-weight: 600; color: var(--jp-ink-50);
    letter-spacing: 0.06em; text-transform: uppercase; margin-top: 3px;
  }
  .jp-stat-sep { width: 1px; height: 36px; background: var(--jp-ink-15); }

  /* ── Main layout ── */
  .jp-main {
    position: relative; z-index: 1;
    max-width: 1200px; margin: 0 auto;
    padding: 2.5rem clamp(1.25rem, 5vw, 3rem) 5rem;
    display: grid; grid-template-columns: 260px 1fr; gap: 2.5rem;
  }
  @media (max-width: 900px) {
    .jp-main { grid-template-columns: 1fr; }
    .jp-sidebar { display: none; }
    .jp-sidebar.open { display: block; }
  }

  /* ── Sidebar filters ── */
  /* ✅ FIX 1: sidebar top offset must account for fixed nav (68px) + small gap */
  .jp-sidebar { position: sticky; top: 80px; height: fit-content; }
  .jp-filter-panel {
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: var(--jp-radius); overflow: hidden; box-shadow: var(--jp-shadow);
  }
  .jp-filter-hd {
    padding: 16px 18px; border-bottom: 1px solid var(--jp-ink-15);
    display: flex; align-items: center; justify-content: space-between;
  }
  .jp-filter-hd-title {
    font-family: 'Playfair Display', serif; font-size: 0.95rem; font-weight: 700;
    color: var(--jp-ink); display: flex; align-items: center; gap: 8px;
  }
  .jp-filter-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 20px; }
  .jp-filter-group {}
  .jp-filter-label {
    font-family: 'DM Mono', monospace; font-size: 0.63rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--jp-ink-50); margin-bottom: 10px;
  }
  .jp-filter-chips { display: flex; flex-direction: column; gap: 6px; }
  .jp-chip {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 12px; border-radius: 8px; cursor: pointer;
    border: 1px solid var(--jp-ink-15); background: transparent;
    font-family: 'DM Sans', sans-serif; font-size: 0.82rem;
    color: var(--jp-ink-80); transition: all 0.16s; text-align: left;
    width: 100%;
  }
  .jp-chip:hover { border-color: var(--jp-gold-bdr); background: var(--jp-gold-bg); }
  .jp-chip.active {
    background: var(--jp-ink); color: var(--jp-cream);
    border-color: var(--jp-ink);
  }
  .jp-chip-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    background: var(--jp-ink-15); transition: background 0.16s;
  }
  .jp-chip.active .jp-chip-dot { background: var(--jp-gold); }
  .jp-clear-btn {
    background: none; border: none; cursor: pointer;
    font-size: 0.78rem; color: var(--jp-gold); font-family: 'DM Sans', sans-serif;
    font-weight: 600; padding: 0; transition: opacity 0.16s;
  }
  .jp-clear-btn:hover { opacity: 0.7; }

  /* ── Content area ── */
  .jp-content {}
  .jp-content-hd {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 1.5rem; flex-wrap: wrap; gap: 12px;
  }
  .jp-results-count {
    font-family: 'Playfair Display', serif; font-size: 1.35rem; font-weight: 700; color: var(--jp-ink);
  }
  .jp-results-count span { color: var(--jp-gold); }
  .jp-sort-select {
    display: flex; align-items: center; gap: 8px;
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: 9px; padding: 8px 14px;
    font-family: 'DM Sans', sans-serif; font-size: 0.82rem; color: var(--jp-ink);
    cursor: pointer; transition: border-color 0.16s;
  }
  .jp-sort-select select {
    border: none; outline: none; background: transparent;
    font-family: 'DM Sans', sans-serif; font-size: 0.82rem; color: var(--jp-ink);
    cursor: pointer;
  }
  .jp-mobile-filter-btn {
    display: none; align-items: center; gap: 7px;
    padding: 9px 16px; background: var(--jp-card-bg);
    border: 1px solid var(--jp-ink-15); border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 0.85rem;
    color: var(--jp-ink); cursor: pointer;
  }
  @media (max-width: 900px) { .jp-mobile-filter-btn { display: flex; } }

  /* ════════════════════════════════════════════════════════
     ✅ FIX 2: JOB CARDS — Mobile-responsive, no clipping
  ════════════════════════════════════════════════════════ */
  .jp-jobs-list { display: flex; flex-direction: column; gap: 1rem; }

  .jp-job-card {
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: var(--jp-radius); padding: 1.5rem;
    cursor: pointer; transition: all 0.22s ease; box-shadow: var(--jp-shadow);
    position: relative;
    /* ✅ Changed from overflow:hidden to overflow:visible so nothing gets clipped */
    overflow: visible;
    animation: jp-fadeUp 0.5s ease both;
  }
  /* Gold top-border via pseudo still works with overflow:visible */
  .jp-job-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--jp-gold), var(--jp-gold-lt));
    opacity: 0; transition: opacity 0.22s;
    border-radius: var(--jp-radius) var(--jp-radius) 0 0;
    z-index: 1;
  }
  .jp-job-card:hover { transform: translateY(-3px); box-shadow: var(--jp-shadow-lg); border-color: var(--jp-gold-bdr); }
  .jp-job-card:hover::before { opacity: 1; }

  .jp-job-card-hd { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 1rem; }
  .jp-company-logo {
    width: 48px; height: 48px; flex-shrink: 0; border-radius: 12px;
    background: var(--jp-ink-07); border: 1px solid var(--jp-ink-15);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Playfair Display', serif; font-size: 1rem; font-weight: 700;
    color: var(--jp-ink-50); overflow: hidden;
  }
  .jp-company-logo img { width: 100%; height: 100%; object-fit: contain; padding: 6px; }
  .jp-job-meta { flex: 1; min-width: 0; }
  .jp-job-title {
    font-family: 'Playfair Display', serif; font-size: 1.05rem; font-weight: 700;
    color: var(--jp-ink); margin-bottom: 4px; line-height: 1.25;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .jp-job-company {
    font-size: 0.85rem; color: var(--jp-ink-80); font-weight: 500;
    display: flex; align-items: center; gap: 5px;
  }
  .jp-job-badges { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 0.85rem; }
  .jp-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 50px; font-size: 0.74rem; font-weight: 500;
  }
  .jp-badge-loc   { background: var(--jp-ink-07); color: var(--jp-ink-80); }
  .jp-badge-type  { background: var(--jp-gold-bg); color: #9A6F1E; border: 1px solid var(--jp-gold-bdr); }
  .jp-badge-salary { background: var(--jp-green-bg); color: var(--jp-green); }
  .jp-badge-new   { background: var(--jp-red-bg); color: var(--jp-red); font-weight: 700; }

  .jp-job-desc {
    font-size: 0.85rem; color: var(--jp-ink-50); line-height: 1.65;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: 1rem;
  }

  /* ✅ FIX 2: Footer — robust flex that wraps cleanly on all screen sizes */
  .jp-job-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    /* Ensure the footer row never gets clipped */
    position: relative;
    z-index: 2;
  }
  .jp-job-age {
    font-family: 'DM Mono', monospace; font-size: 0.68rem;
    color: var(--jp-ink-50); letter-spacing: 0.05em;
    display: flex; align-items: center; gap: 5px;
    flex-shrink: 0;
  }
  .jp-apply-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 10px 22px;
    background: var(--jp-ink); color: var(--jp-cream);
    border-radius: 50px; font-size: 0.83rem; font-weight: 600;
    text-decoration: none; transition: all 0.18s; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    flex-shrink: 0;
    white-space: nowrap;
    /* ✅ Ensure button never collapses smaller than its text */
    min-width: 130px;
  }
  .jp-apply-btn:hover {
    background: var(--jp-gold); color: var(--jp-ink);
    transform: translateY(-1px); box-shadow: 0 6px 20px rgba(200,151,58,0.35);
  }

  /* ✅ FIX 2: Mobile — stack footer, full-width button, comfortable padding */
  @media (max-width: 520px) {
    .jp-job-card {
      padding: 1.1rem 1rem;
    }
    .jp-job-title {
      font-size: 0.96rem;
    }
    .jp-company-logo {
      width: 40px; height: 40px; font-size: 0.88rem; border-radius: 10px;
    }
    .jp-job-footer {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }
    .jp-apply-btn {
      width: 100%;
      min-width: unset;
      padding: 13px 20px;
      font-size: 0.88rem;
      border-radius: 12px;
    }
    .jp-job-age {
      font-size: 0.66rem;
    }
  }

  .jp-save-btn {
    width: 34px; height: 34px; border-radius: 50%;
    border: 1px solid var(--jp-ink-15); background: transparent;
    color: var(--jp-ink-50); display: flex; align-items: center;
    justify-content: center; cursor: pointer; transition: all 0.16s;
    flex-shrink: 0;
  }
  .jp-save-btn:hover { border-color: var(--jp-gold-bdr); color: var(--jp-gold); background: var(--jp-gold-bg); }
  .jp-save-btn.saved { background: var(--jp-gold-bg); color: var(--jp-gold); border-color: var(--jp-gold-bdr); }

  /* ── AD NUDGE CARD ── */
  .jp-ad-nudge {
    background: var(--jp-ink); border-radius: var(--jp-radius);
    padding: 1.75rem; position: relative; overflow: hidden;
    animation: jp-fadeUp 0.5s ease both;
  }
  .jp-ad-nudge::before {
    content: ''; position: absolute; bottom: -20px; right: -20px;
    width: 140px; height: 140px; border-radius: 50%;
    background: rgba(200,151,58,0.12); pointer-events: none;
  }
  .jp-ad-nudge::after {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--jp-gold), var(--jp-gold-lt));
  }
  .jp-ad-nudge-eyebrow {
    font-family: 'DM Mono', monospace; font-size: 0.65rem; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--jp-gold); margin-bottom: 0.65rem;
    display: flex; align-items: center; gap: 7px;
  }
  .jp-ad-nudge-title {
    font-family: 'Playfair Display', serif; font-size: 1.1rem; font-weight: 700;
    color: #FAF8F5; line-height: 1.3; margin-bottom: 0.6rem;
  }
  .jp-ad-nudge-sub {
    font-size: 0.82rem; color: rgba(250,248,245,0.55);
    line-height: 1.6; margin-bottom: 1.25rem;
  }
  .jp-ad-nudge-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; background: var(--jp-gold); color: var(--jp-ink);
    border-radius: 50px; font-size: 0.85rem; font-weight: 700;
    text-decoration: none; cursor: pointer; border: none;
    font-family: 'DM Sans', sans-serif; transition: all 0.2s;
  }
  .jp-ad-nudge-btn:hover { background: var(--jp-gold-lt); transform: translateY(-1px); }

  /* ── Skeleton loading ── */
  .jp-skeleton { background: var(--jp-ink-07); border-radius: 8px; overflow: hidden; position: relative; }
  .jp-skeleton::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: jp-shimmer 1.4s infinite;
  }
  @keyframes jp-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
  .jp-skeleton-card {
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: var(--jp-radius); padding: 1.5rem; box-shadow: var(--jp-shadow);
  }

  /* ── Error / Empty ── */
  .jp-empty {
    text-align: center; padding: 5rem 2rem;
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    border-radius: var(--jp-radius); box-shadow: var(--jp-shadow);
  }
  .jp-empty-icon {
    width: 60px; height: 60px; border-radius: 16px;
    background: var(--jp-ink-07); color: var(--jp-ink-50);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 1.25rem;
  }
  .jp-empty-title {
    font-family: 'Playfair Display', serif; font-size: 1.25rem;
    font-weight: 700; color: var(--jp-ink); margin-bottom: 0.5rem;
  }
  .jp-empty-sub { font-size: 0.88rem; color: var(--jp-ink-50); line-height: 1.6; }

  /* ── Pagination ── */
  .jp-pagination {
    display: flex; align-items: center; justify-content: center;
    gap: 8px; margin-top: 2.5rem; flex-wrap: wrap;
  }
  .jp-pg-btn {
    width: 38px; height: 38px; border-radius: 9px;
    border: 1px solid var(--jp-ink-15); background: var(--jp-card-bg);
    color: var(--jp-ink-80); display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-size: 0.85rem; font-weight: 600;
    font-family: 'DM Sans', sans-serif; transition: all 0.16s;
  }
  .jp-pg-btn:hover { border-color: var(--jp-gold-bdr); background: var(--jp-gold-bg); color: var(--jp-gold); }
  .jp-pg-btn.active { background: var(--jp-ink); color: var(--jp-cream); border-color: var(--jp-ink); }
  .jp-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }

  /* ── Mobile drawer ── */
  .jp-filter-drawer-backdrop {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(26,24,20,0.5);
    backdrop-filter: blur(6px); animation: jp-fadeIn 0.2s ease;
  }
  .jp-filter-drawer {
    position: fixed; left: 0; top: 0; bottom: 0; width: min(320px, 92vw);
    background: var(--jp-cream); z-index: 201; overflow-y: auto;
    box-shadow: var(--jp-shadow-lg); padding: 1.5rem;
    animation: jp-slideRight 0.28s cubic-bezier(0.22,1,0.36,1) both;
  }
  @keyframes jp-slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }
  .jp-drawer-close {
    position: absolute; top: 1rem; right: 1rem;
    width: 36px; height: 36px; border-radius: 50%;
    border: 1px solid var(--jp-ink-15); background: transparent;
    color: var(--jp-ink-50); display: flex; align-items: center;
    justify-content: center; cursor: pointer; transition: all 0.16s;
  }
  .jp-drawer-close:hover { background: var(--jp-ink); color: var(--jp-cream); border-color: var(--jp-ink); }

  /* ════════════════════════════════════════════════════════
     APPLY INTERSTITIAL MODAL
  ════════════════════════════════════════════════════════ */

  .jp-modal-backdrop {
    position: fixed; inset: 0; z-index: 500;
    background: rgba(26,24,20,0.72);
    backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
    padding: clamp(1rem, 4vw, 2rem);
    animation: jp-fadeIn 0.25s ease both;
  }

  .jp-modal {
    background: var(--jp-card-bg);
    border-radius: 24px;
    width: 100%;
    max-width: 860px;
    max-height: 92vh;
    overflow-y: auto;
    box-shadow: 0 32px 80px rgba(26,24,20,0.28), 0 0 0 1px rgba(26,24,20,0.08);
    display: grid;
    grid-template-columns: 1fr 1fr;
    position: relative;
    animation: jp-modalIn 0.35s cubic-bezier(0.22,1,0.36,1) both;
    -ms-overflow-style: none; scrollbar-width: none;
  }
  .jp-modal::-webkit-scrollbar { display: none; }

  @keyframes jp-modalIn {
    from { opacity: 0; transform: scale(0.93) translateY(24px); }
    to   { opacity: 1; transform: scale(1)    translateY(0); }
  }

  .jp-modal-left {
    background: var(--jp-ink);
    border-radius: 24px 0 0 24px;
    padding: 2.5rem 2rem;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    position: relative; overflow: hidden;
    min-height: 540px;
  }
  .jp-modal-left::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(ellipse at 20% 80%, rgba(200,151,58,0.18) 0%, transparent 55%),
      radial-gradient(ellipse at 80% 10%, rgba(200,151,58,0.10) 0%, transparent 50%);
    pointer-events: none;
  }
  .jp-modal-left::after {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--jp-gold), var(--jp-gold-lt), var(--jp-gold));
  }

  .jp-modal-particle {
    position: absolute; border-radius: 50%;
    background: var(--jp-gold); opacity: 0.18;
    animation: jp-float linear infinite;
    pointer-events: none;
  }
  @keyframes jp-float {
    0%   { transform: translateY(0px) rotate(0deg); opacity: 0.18; }
    50%  { opacity: 0.08; }
    100% { transform: translateY(-120px) rotate(180deg); opacity: 0; }
  }

  .jp-modal-preview-label {
    font-family: 'DM Mono', monospace;
    font-size: 0.6rem; font-weight: 500; letter-spacing: 0.15em;
    text-transform: uppercase; color: var(--jp-gold);
    margin-bottom: 1rem; position: relative; z-index: 1;
    display: flex; align-items: center; gap: 7px;
  }
  .jp-modal-preview-label::before,
  .jp-modal-preview-label::after {
    content: ''; flex: 1; height: 1px;
    background: rgba(200,151,58,0.3);
  }

  .jp-modal-resume-wrap {
    position: relative; z-index: 1;
    width: 100%; max-width: 280px;
  }

  .jp-modal-resume-glow {
    position: absolute; inset: -16px;
    border-radius: 18px;
    background: radial-gradient(ellipse, rgba(200,151,58,0.22) 0%, transparent 70%);
    filter: blur(20px);
    animation: jp-glowPulse 2.8s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes jp-glowPulse {
    0%, 100% { opacity: 0.7; transform: scale(1); }
    50%       { opacity: 1; transform: scale(1.05); }
  }

  .jp-modal-resume-badge {
    position: absolute;
    top: -14px; right: -14px;
    background: var(--jp-gold);
    color: var(--jp-ink);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.7rem; font-weight: 700;
    padding: 6px 12px; border-radius: 50px;
    box-shadow: 0 4px 14px rgba(200,151,58,0.45);
    animation: jp-badgeBounce 2s ease-in-out infinite;
    white-space: nowrap; z-index: 2;
    display: flex; align-items: center; gap: 5px;
  }
  @keyframes jp-badgeBounce {
    0%, 100% { transform: translateY(0) rotate(-3deg); }
    50%       { transform: translateY(-5px) rotate(-3deg); }
  }

  .jp-modal-ats-badge {
    position: absolute;
    bottom: -14px; left: -14px;
    background: #fff;
    border: 1.5px solid var(--jp-green);
    color: var(--jp-green);
    font-family: 'DM Mono', monospace;
    font-size: 0.65rem; font-weight: 600;
    padding: 5px 11px; border-radius: 50px;
    box-shadow: 0 4px 14px rgba(39,174,96,0.2);
    animation: jp-badgeBounce2 2.4s ease-in-out infinite;
    display: flex; align-items: center; gap: 5px;
    z-index: 2;
  }
  @keyframes jp-badgeBounce2 {
    0%, 100% { transform: translateY(0) rotate(2deg); }
    50%       { transform: translateY(-4px) rotate(2deg); }
  }

  .jp-modal-resume-img {
    width: 100%;
    border-radius: 10px;
    box-shadow: 0 20px 60px rgba(26,24,20,0.5), 0 0 0 1px rgba(255,255,255,0.08);
    display: block;
    transition: transform 0.4s ease;
  }
  .jp-modal-resume-wrap:hover .jp-modal-resume-img {
    transform: scale(1.02) translateY(-3px);
  }

  .jp-modal-right {
    padding: clamp(1.75rem, 4vw, 2.5rem);
    display: flex; flex-direction: column;
    justify-content: center;
  }

  .jp-modal-close {
    position: absolute; top: 16px; right: 16px;
    width: 36px; height: 36px; border-radius: 50%;
    border: 1px solid var(--jp-ink-15); background: var(--jp-card-bg);
    color: var(--jp-ink-50); display: flex; align-items: center;
    justify-content: center; cursor: pointer; transition: all 0.18s;
    z-index: 10;
  }
  .jp-modal-close:hover {
    background: var(--jp-ink); color: var(--jp-cream); border-color: var(--jp-ink);
    transform: rotate(90deg);
  }

  .jp-modal-eyebrow {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 5px 13px; border-radius: 50px;
    background: var(--jp-gold-bg); border: 1px solid var(--jp-gold-bdr);
    color: #9A6F1E; font-size: 0.72rem; font-weight: 600;
    letter-spacing: 0.03em; margin-bottom: 1.2rem;
    width: fit-content;
    animation: jp-fadeUp 0.4s ease 0.15s both;
  }

  .jp-modal-job-ctx {
    display: flex; align-items: center; gap: 8px;
    background: var(--jp-ink-07); border: 1px solid var(--jp-ink-15);
    border-radius: 10px; padding: 10px 14px;
    margin-bottom: 1.5rem;
    animation: jp-fadeUp 0.4s ease 0.1s both;
  }
  .jp-modal-job-ctx-logo {
    width: 36px; height: 36px; border-radius: 8px;
    background: var(--jp-card-bg); border: 1px solid var(--jp-ink-15);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Playfair Display', serif; font-size: 0.82rem; font-weight: 700;
    color: var(--jp-ink-50); flex-shrink: 0;
  }
  .jp-modal-job-ctx-text { flex: 1; min-width: 0; }
  .jp-modal-job-ctx-title {
    font-size: 0.82rem; font-weight: 600; color: var(--jp-ink);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .jp-modal-job-ctx-co {
    font-size: 0.74rem; color: var(--jp-ink-50);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .jp-modal-job-ctx-badge {
    font-family: 'DM Mono', monospace; font-size: 0.6rem; font-weight: 600;
    letter-spacing: 0.08em; color: var(--jp-green);
    background: var(--jp-green-bg); border-radius: 6px;
    padding: 3px 8px; flex-shrink: 0;
  }

  .jp-modal-headline {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(1.4rem, 3vw, 1.9rem);
    font-weight: 900; line-height: 1.12; letter-spacing: -0.5px;
    color: var(--jp-ink); margin-bottom: 0.75rem;
    animation: jp-fadeUp 0.4s ease 0.2s both;
  }
  .jp-modal-headline em { font-style: italic; color: var(--jp-gold); }

  .jp-modal-sub {
    font-size: 0.88rem; color: var(--jp-ink-50); line-height: 1.65;
    margin-bottom: 1.5rem;
    animation: jp-fadeUp 0.4s ease 0.25s both;
  }

  .jp-modal-features {
    display: flex; flex-direction: column; gap: 9px;
    margin-bottom: 1.75rem;
    animation: jp-fadeUp 0.4s ease 0.3s both;
  }
  .jp-modal-feature {
    display: flex; align-items: center; gap: 9px;
    font-size: 0.83rem; color: var(--jp-ink-80);
  }
  .jp-modal-feature-icon {
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--jp-green-bg); color: var(--jp-green);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .jp-modal-cta-wrap {
    display: flex; flex-direction: column; gap: 10px;
    animation: jp-fadeUp 0.4s ease 0.35s both;
  }
  .jp-modal-cta-primary {
    display: flex; align-items: center; justify-content: center; gap: 9px;
    padding: 14px 24px; background: var(--jp-ink); color: var(--jp-cream);
    border-radius: 14px; font-family: 'DM Sans', sans-serif;
    font-size: 0.92rem; font-weight: 700; cursor: pointer; border: none;
    transition: all 0.22s; text-decoration: none; line-height: 1;
    position: relative; overflow: hidden;
  }
  .jp-modal-cta-primary::before {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, var(--jp-gold) 0%, var(--jp-gold-lt) 100%);
    opacity: 0; transition: opacity 0.22s;
  }
  .jp-modal-cta-primary:hover::before { opacity: 1; }
  .jp-modal-cta-primary:hover { color: var(--jp-ink); transform: translateY(-2px); box-shadow: 0 10px 32px rgba(200,151,58,0.4); }
  .jp-modal-cta-primary span { position: relative; z-index: 1; }
  .jp-modal-cta-primary svg { position: relative; z-index: 1; }

  .jp-modal-cta-skip {
    display: flex; align-items: center; justify-content: center; gap: 7px;
    padding: 11px 24px; background: transparent; color: var(--jp-ink-50);
    border-radius: 14px; font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem; font-weight: 500; cursor: pointer;
    border: 1px solid var(--jp-ink-15); transition: all 0.18s; text-decoration: none;
  }
  .jp-modal-cta-skip:hover {
    background: var(--jp-ink-07); border-color: var(--jp-ink-50);
    color: var(--jp-ink-80);
  }

  .jp-modal-skip-timer {
    font-family: 'DM Mono', monospace; font-size: 0.72rem;
    color: var(--jp-ink-50); margin-top: 8px; text-align: center;
  }
  .jp-modal-skip-timer span { color: var(--jp-gold); font-weight: 600; }

  .jp-modal-social {
    display: flex; align-items: center; gap: 8px; margin-top: 1rem;
    font-size: 0.76rem; color: var(--jp-ink-50);
    animation: jp-fadeUp 0.4s ease 0.4s both;
  }
  .jp-modal-avatars { display: flex; }
  .jp-modal-avatar {
    width: 24px; height: 24px; border-radius: 50%;
    border: 2px solid var(--jp-card-bg);
    background: var(--jp-gold-bg); color: #9A6F1E;
    font-size: 0.55rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
    margin-left: -6px; font-family: 'DM Sans', sans-serif;
  }
  .jp-modal-avatars .jp-modal-avatar:first-child { margin-left: 0; }
  .jp-modal-stars {
    display: flex; align-items: center; gap: 2px; color: var(--jp-gold);
  }

  @media (max-width: 680px) {
    .jp-modal {
      grid-template-columns: 1fr;
      max-width: 420px;
      max-height: 90vh;
    }
    .jp-modal-left {
      border-radius: 24px 24px 0 0;
      min-height: 280px;
      padding: 2rem 1.5rem 1.5rem;
    }
    .jp-modal-right {
      border-radius: 0 0 24px 24px;
      padding: 1.5rem;
    }
    .jp-modal-resume-wrap { max-width: 180px; }
    .jp-modal-headline { font-size: 1.35rem; }
  }

  /* ── Animations ── */
  @keyframes jp-fadeUp  { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes jp-fadeIn  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes jp-spin    { to { transform: rotate(360deg); } }
  .jp-spin { animation: jp-spin 0.8s linear infinite; }

  /* ── Footer ── */
  .jp-footer {
    background: var(--jp-cream); border-top: 1px solid var(--jp-ink-15);
    padding: 2rem clamp(1.25rem, 5vw, 3rem);
    text-align: center; position: relative; z-index: 1;
  }
  .jp-footer-copy { font-size: 0.8rem; color: var(--jp-ink-50); }
  .jp-footer-cta {
    margin-top: 0.6rem; font-size: 0.82rem; color: var(--jp-ink-80);
  }
  .jp-footer-cta a {
    color: var(--jp-gold); font-weight: 600; text-decoration: none;
    border-bottom: 1px solid var(--jp-gold-bdr); transition: border-color 0.16s;
  }
  .jp-footer-cta a:hover { border-color: var(--jp-gold); }

  /* ── Small screen search bar ── */
  @media (max-width: 640px) {
    .jp-search-bar { flex-wrap: wrap; padding: 10px; }
    .jp-search-divider { display: none; }
    .jp-search-location { display: none; }
    .jp-search-btn { width: 100%; justify-content: center; border-radius: 10px; }
    .jp-stats-strip { gap: 1rem; }
    .jp-hero-h1 { letter-spacing: -0.5px; }
    .jp-stat-sep { display: none; }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('jp-styles')) {
  const s = document.createElement('style');
  s.id = 'jp-styles';
  s.textContent = JP_STYLES;
  document.head.appendChild(s);
}

/* ════════════════════════════════════════════════════════════════════
   ADZUNA API SERVICE
════════════════════════════════════════════════════════════════════ */
const ADZUNA_APP_ID  = import.meta.env.VITE_ADZUNA_APP_ID || '';
const ADZUNA_APP_KEY = import.meta.env.VITE_ADZUNA_APP_KEY || '';
const JOOBLE_KEY     = import.meta.env.VITE_JOOBLE_API_KEY || '';

const CATEGORY_MAP = {
  'All Domains':        null,
  'Software & Tech':    'it-jobs',
  'Data & ML':          'scientific-qa-jobs',
  'Design & UX':        'creative-design-jobs',
  'Product':            'consultancy-jobs',
  'Finance':            'accounting-finance-jobs',
  'Marketing':          'pr-advertising-marketing-jobs',
  'Sales':              'sales-jobs',
  'Operations':         'logistics-warehouse-jobs',
};

const CONTRACT_MAP = {
  'All Types':  null,
  'Full-time':  'permanent',
  'Part-time':  'part_time',
  'Contract':   'contract',
  'Internship': 'internship',
};

async function fetchAdzunaJobs({ keyword = '', location = '', category, contractType, page = 1 }) {
  if (!ADZUNA_APP_ID || !ADZUNA_APP_KEY) {
    return fetchMockJobs({ keyword, location, category, contractType, page });
  }
  const country = 'in';
  const base = `https://api.adzuna.com/v1/api/jobs/${country}/search/${page}`;
  const params = new URLSearchParams({
    app_id: ADZUNA_APP_ID,
    app_key: ADZUNA_APP_KEY,
    results_per_page: 10,
    'content-type': 'application/json',
  });
  if (keyword)      params.set('what', keyword);
  if (location)     params.set('where', location);
  if (category)     params.set('category', category);
  if (contractType) params.set('contract_type', contractType);
  params.set('sort_by', 'date');
  params.set('sort_direction', 'down');
  const res = await fetch(`${base}?${params}`);
  if (!res.ok) throw new Error(`Adzuna API error: ${res.status}`);
  const data = await res.json();
  return {
    jobs: (data.results || []).map(normaliseAdzuna),
    total: data.count || 0,
  };
}

async function fetchJoobleJobs({ keyword = '', location = '', page = 1 }) {
  if (!JOOBLE_KEY) return { jobs: [], total: 0 };
  const res = await fetch(`https://jooble.org/api/${JOOBLE_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keywords: keyword, location: location || 'India', page }),
  });
  if (!res.ok) return { jobs: [], total: 0 };
  const data = await res.json();
  return {
    jobs: (data.jobs || []).map(normaliseJooble),
    total: data.totalCount || 0,
  };
}

function normaliseAdzuna(j) {
  return {
    id:          j.id,
    title:       j.title || 'Software Engineer',
    company:     j.company?.display_name || 'Company',
    location:    j.location?.display_name || 'Remote',
    description: j.description || '',
    salary:      formatSalary(j.salary_min, j.salary_max),
    contractType:j.contract_type || 'permanent',
    category:    j.category?.label || 'Technology',
    url:         j.redirect_url || '#',
    postedAt:    j.created || new Date().toISOString(),
    isNew:       isNew(j.created),
    logoUrl:     null,
  };
}

function normaliseJooble(j) {
  return {
    id:          j.id || Math.random().toString(36).slice(2),
    title:       j.title || 'Position',
    company:     j.company || 'Company',
    location:    j.location || 'India',
    description: j.snippet || '',
    salary:      j.salary || '',
    contractType:'permanent',
    category:    'General',
    url:         j.link || '#',
    postedAt:    j.updated || new Date().toISOString(),
    isNew:       isNew(j.updated),
    logoUrl:     null,
  };
}

function formatSalary(min, max) {
  if (!min && !max) return '';
  const fmt = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${Math.round(n / 1000)}K`;
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  if (min) return `From ${fmt(min)}`;
  return `Up to ${fmt(max)}`;
}

function isNew(dateStr) {
  if (!dateStr) return false;
  const posted = new Date(dateStr);
  const diff = (Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24);
  return diff <= 3;
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Just now';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 604800)}w ago`;
}

/* ════════════════════════════════════════════════════════════════════
   MOCK DATA
════════════════════════════════════════════════════════════════════ */
const MOCK_JOBS = [
  { id: '1', title: 'Senior Frontend Engineer', company: 'Razorpay', location: 'Bengaluru, KA', description: 'Build world-class payment experiences with React, TypeScript, and modern web technologies. Work alongside brilliant engineers in a high-growth fintech environment.', salary: '₹20L – ₹35L', contractType: 'permanent', category: 'Technology', url: 'https://razorpay.com/jobs', postedAt: new Date(Date.now() - 86400000).toISOString(), isNew: true },
  { id: '2', title: 'Product Manager – Growth', company: 'Swiggy', location: 'Bengaluru, KA', description: 'Drive product strategy for our growth initiatives. You will own roadmaps, work with data science, and directly influence how millions of users experience food delivery.', salary: '₹25L – ₹40L', contractType: 'permanent', category: 'Product', url: 'https://swiggy.com/careers', postedAt: new Date(Date.now() - 172800000).toISOString(), isNew: true },
  { id: '3', title: 'Data Scientist – Recommendations', company: 'Flipkart', location: 'Bengaluru, KA', description: 'Use machine learning to power personalised product recommendations across Flipkart\'s 350M+ customer base. Strong Python and ML framework experience required.', salary: '₹18L – ₹32L', contractType: 'permanent', category: 'Data & ML', url: 'https://flipkart.com/jobs', postedAt: new Date(Date.now() - 259200000).toISOString(), isNew: false },
  { id: '4', title: 'UX Designer – Mobile', company: 'CRED', location: 'Bengaluru, KA', description: 'Craft elegant, delightful mobile experiences for India\'s most design-conscious fintech app. 3+ years of product design experience with a strong Figma portfolio required.', salary: '₹15L – ₹25L', contractType: 'permanent', category: 'Design & UX', url: 'https://cred.club/careers', postedAt: new Date(Date.now() - 345600000).toISOString(), isNew: false },
  { id: '5', title: 'Backend Engineer – Platform', company: 'Zepto', location: 'Mumbai, MH', description: 'Build the distributed systems that power 10-minute grocery delivery at massive scale. Experience with Go, Kafka, and high-throughput microservices is a big plus.', salary: '₹22L – ₹38L', contractType: 'permanent', category: 'Technology', url: 'https://zepto.com/jobs', postedAt: new Date(Date.now() - 432000000).toISOString(), isNew: false },
  { id: '6', title: 'SDE-2 – Android', company: 'PhonePe', location: 'Bengaluru, KA', description: 'Build world-class native Android features for 500M+ PhonePe users. We\'re looking for engineers who care deeply about performance, architecture, and user experience.', salary: '₹24L – ₹42L', contractType: 'permanent', category: 'Technology', url: 'https://phonepe.com/careers', postedAt: new Date(Date.now() - 518400000).toISOString(), isNew: false },
  { id: '7', title: 'ML Engineer – NLP', company: 'Sarvam AI', location: 'Bengaluru, KA', description: 'Work on cutting-edge NLP models for Indian languages. You\'ll pretrain and fine-tune large language models, own evaluation pipelines, and ship production ML systems.', salary: '₹30L – ₹55L', contractType: 'permanent', category: 'Data & ML', url: 'https://sarvam.ai/careers', postedAt: new Date(Date.now() - 604800000).toISOString(), isNew: false },
  { id: '8', title: 'Marketing Lead – B2B SaaS', company: 'Freshworks', location: 'Chennai, TN', description: 'Own the go-to-market strategy for Freshdesk\'s enterprise product line. Drive pipeline through content, events, and ABM campaigns targeting global enterprise accounts.', salary: '₹18L – ₹28L', contractType: 'permanent', category: 'Marketing', url: 'https://freshworks.com/company/careers', postedAt: new Date(Date.now() - 691200000).toISOString(), isNew: false },
  { id: '9', title: 'DevOps Engineer – Cloud', company: 'Atlassian', location: 'Remote', description: 'Join our SRE team to build and maintain the infrastructure that powers Jira, Confluence, and Bitbucket for millions of teams. AWS, Terraform, and Kubernetes expertise needed.', salary: '₹28L – ₹48L', contractType: 'permanent', category: 'Technology', url: 'https://atlassian.com/company/careers', postedAt: new Date(Date.now() - 777600000).toISOString(), isNew: false },
  { id: '10', title: 'Finance Analyst – FP&A', company: 'Meesho', location: 'Bengaluru, KA', description: 'Support strategic financial planning and analysis for one of India\'s fastest-growing e-commerce platforms. CA/MBA with 2-4 years of FP&A experience preferred.', salary: '₹12L – ₹20L', contractType: 'permanent', category: 'Finance', url: 'https://meesho.com/jobs', postedAt: new Date(Date.now() - 864000000).toISOString(), isNew: false },
];

async function fetchMockJobs({ keyword, location, category, contractType, page }) {
  await new Promise(r => setTimeout(r, 800));
  let results = [...MOCK_JOBS];
  if (keyword) results = results.filter(j =>
    j.title.toLowerCase().includes(keyword.toLowerCase()) ||
    j.company.toLowerCase().includes(keyword.toLowerCase()) ||
    j.description.toLowerCase().includes(keyword.toLowerCase())
  );
  if (category && category !== 'All Domains') {
    results = results.filter(j => j.category === category);
  }
  const start = (page - 1) * 10;
  return { jobs: results.slice(start, start + 10), total: results.length };
}

/* ════════════════════════════════════════════════════════════════════
   APPLY INTERSTITIAL MODAL
════════════════════════════════════════════════════════════════════ */

const MODAL_HOOKS = [
  {
    eyebrow: "Don't miss this opportunity",
    headline: <>Before you apply — <em>stand out</em> from the crowd.</>,
    sub: "Recruiters spend 6 seconds on a resume. Make every second count with an ATS-optimised resume built for this exact role.",
    cta: "Build My Winning Resume",
  },
  {
    eyebrow: "Grab this opportunity",
    headline: <>You found the role. Now <em>seal the deal.</em></>,
    sub: "75% of resumes are rejected before a human reads them. Beat the ATS with a recruiter-ready resume — ready in minutes.",
    cta: "Get Past the ATS Now",
  },
  {
    eyebrow: "Your dream job is one step away",
    headline: <><em>Land the interview,</em> not just the application.</>,
    sub: "A tailored, polished resume doubles your callback rate. TypeMyCv's proven templates are trusted by engineers at FAANG, Razorpay, and more.",
    cta: "Create My Resume Free",
  },
];

const FEATURES = [
  "ATS-optimised — beats automated filters",
  "Built in under 5 minutes, no design skill needed",
  "Trusted by 50,000+ job seekers across India",
];

const AVATARS = ['RK', 'AS', 'PD', 'MV', 'NJ'];

const PARTICLES = [
  { size: 6,  left: '15%', delay: '0s',    duration: '5s'  },
  { size: 4,  left: '70%', delay: '1.2s',  duration: '7s'  },
  { size: 8,  left: '40%', delay: '2.5s',  duration: '6s'  },
  { size: 5,  left: '85%', delay: '0.8s',  duration: '8s'  },
  { size: 3,  left: '25%', delay: '3.5s',  duration: '5.5s'},
];

const ApplyModal = ({ job, onClose, onBuildResume, hookIndex }) => {
  const [countdown, setCountdown] = useState(5);
  const [canSkip, setCanSkip] = useState(false);
  const hook = MODAL_HOOKS[hookIndex % MODAL_HOOKS.length];
  const initials = job.company.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

  useEffect(() => {
    if (countdown <= 0) { setCanSkip(true); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && canSkip) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canSkip, onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      className="jp-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && canSkip) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Build your resume before applying"
    >
      <div className="jp-modal">

        {/* ── Left: Resume Preview ── */}
        <div className="jp-modal-left">
          {PARTICLES.map((p, i) => (
            <div
              key={i}
              className="jp-modal-particle"
              style={{
                width: p.size, height: p.size,
                left: p.left, bottom: '10%',
                animationDelay: p.delay,
                animationDuration: p.duration,
              }}
            />
          ))}

          <div className="jp-modal-preview-label">
            <Sparkles size={10} />
            Resume Preview
            <Sparkles size={10} />
          </div>

          <div className="jp-modal-resume-wrap">
            <div className="jp-modal-resume-glow" />
            <div className="jp-modal-resume-badge">
              <Zap size={11} /> ATS Ready
            </div>
            <div className="jp-modal-ats-badge">
              <CheckCircle2 size={10} /> 98% pass rate
            </div>
            <img
              src="/img.jpg"
              alt="Jake's Resume Preview — ATS-optimised template"
              className="jp-modal-resume-img"
            />
          </div>
        </div>

        {/* ── Right: CTA ── */}
        <div className="jp-modal-right">

          <button
            className="jp-modal-close"
            onClick={canSkip ? onClose : undefined}
            aria-label="Close modal"
            title={canSkip ? 'Close' : `Wait ${countdown}s…`}
            style={{ opacity: canSkip ? 1 : 0.4, cursor: canSkip ? 'pointer' : 'not-allowed' }}
          >
            <X size={15} />
          </button>

          <div className="jp-modal-eyebrow">
            <Sparkles size={11} /> {hook.eyebrow}
          </div>

          <div className="jp-modal-job-ctx">
            <div className="jp-modal-job-ctx-logo">{initials}</div>
            <div className="jp-modal-job-ctx-text">
              <div className="jp-modal-job-ctx-title">{job.title}</div>
              <div className="jp-modal-job-ctx-co">{job.company} · {job.location}</div>
            </div>
            <div className="jp-modal-job-ctx-badge">OPEN</div>
          </div>

          <h2 className="jp-modal-headline">{hook.headline}</h2>
          <p className="jp-modal-sub">{hook.sub}</p>

          <div className="jp-modal-features">
            {FEATURES.map((f, i) => (
              <div className="jp-modal-feature" key={i}>
                <div className="jp-modal-feature-icon">
                  <CheckCircle2 size={12} />
                </div>
                {f}
              </div>
            ))}
          </div>

          <div className="jp-modal-cta-wrap">
            <button className="jp-modal-cta-primary" onClick={onBuildResume}>
              <span>{hook.cta}</span>
              <ArrowRight size={15} />
            </button>

            <a
              className="jp-modal-cta-skip"
              href={job.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={canSkip ? onClose : (e) => e.preventDefault()}
              style={{ opacity: canSkip ? 1 : 0.5, pointerEvents: canSkip ? 'auto' : 'none' }}
            >
              <ExternalLink size={13} />
              Skip — Apply directly
            </a>

            {!canSkip && (
              <div className="jp-modal-skip-timer">
                Skip available in <span>{countdown}s</span>
              </div>
            )}
          </div>

          <div className="jp-modal-social">
            <div className="jp-modal-avatars">
              {AVATARS.map((a, i) => (
                <div className="jp-modal-avatar" key={i}>{a}</div>
              ))}
            </div>
            <div className="jp-modal-stars">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={11} fill="currentColor" />
              ))}
            </div>
            <span>50,000+ resumes built</span>
          </div>

        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   SKELETON CARD
════════════════════════════════════════════════════════════════════ */
const SkeletonCard = () => (
  <div className="jp-skeleton-card">
    <div style={{ display: 'flex', gap: 14, marginBottom: 14, alignItems: 'flex-start' }}>
      <div className="jp-skeleton" style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="jp-skeleton" style={{ height: 16, width: '60%', marginBottom: 8, borderRadius: 6 }} />
        <div className="jp-skeleton" style={{ height: 13, width: '40%', borderRadius: 6 }} />
      </div>
    </div>
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      {[80, 90, 70].map((w, i) => <div key={i} className="jp-skeleton" style={{ height: 24, width: w, borderRadius: 50 }} />)}
    </div>
    <div className="jp-skeleton" style={{ height: 13, width: '100%', marginBottom: 6, borderRadius: 6 }} />
    <div className="jp-skeleton" style={{ height: 13, width: '85%', borderRadius: 6 }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, alignItems: 'center' }}>
      <div className="jp-skeleton" style={{ height: 13, width: 70, borderRadius: 6 }} />
      <div className="jp-skeleton" style={{ height: 34, width: 110, borderRadius: 50 }} />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   JOB CARD
════════════════════════════════════════════════════════════════════ */
const JobCard = ({ job, style, onApplyClick }) => {
  const [saved, setSaved] = useState(false);
  const initials = job.company.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

  return (
    <div className="jp-job-card" style={style}>
      <div className="jp-job-card-hd">
        <div className="jp-company-logo">
          {job.logoUrl
            ? <img src={job.logoUrl} alt={job.company} onError={e => { e.currentTarget.style.display = 'none'; }} />
            : initials}
        </div>
        <div className="jp-job-meta">
          <div className="jp-job-title" title={job.title}>{job.title}</div>
          <div className="jp-job-company"><Building2 size={13} />{job.company}</div>
        </div>
        <button
          className={`jp-save-btn${saved ? ' saved' : ''}`}
          onClick={() => setSaved(!saved)}
          aria-label={saved ? 'Unsave job' : 'Save job'}
          title={saved ? 'Saved' : 'Save'}
        >
          <Star size={15} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      <div className="jp-job-badges">
        <span className="jp-badge jp-badge-loc"><MapPin size={11} />{job.location}</span>
        <span className="jp-badge jp-badge-type"><Briefcase size={11} />
          {job.contractType === 'permanent' ? 'Full-time' : job.contractType}
        </span>
        {job.salary && <span className="jp-badge jp-badge-salary"><DollarSign size={11} />{job.salary}</span>}
        {job.isNew && <span className="jp-badge jp-badge-new"><Zap size={11} />NEW</span>}
      </div>

      <p className="jp-job-desc">{job.description}</p>

      <div className="jp-job-footer">
        <span className="jp-job-age"><Clock size={12} />{timeAgo(job.postedAt)}</span>
        <button
          className="jp-apply-btn"
          onClick={() => onApplyClick(job)}
        >
          Apply Now <ExternalLink size={13} />
        </button>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   AD NUDGE CARD (inline between listings)
════════════════════════════════════════════════════════════════════ */
const AD_NUDGES = [
  { headline: "HR screens this job in under 6 seconds.", sub: "Is your resume ready? Most get rejected before a human even reads them.", cta: "Fix It Instantly" },
  { headline: "ATS filters block 75% of resumes.", sub: "Don't let a robot end your job search. Beat the bots with a proven template.", cta: "Build ATS Resume" },
  { headline: "You've found the role. Now get the interview.", sub: "A recruiter-ready resume built in minutes — no design skills needed.", cta: "Create My Resume" },
  { headline: "Great jobs deserve great resumes.", sub: "Jake's Resume template — the #1 ATS-tested layout trusted by FAANG engineers.", cta: "Get Started Free" },
];

const AdNudge = ({ index, onNavigate }) => {
  const nudge = AD_NUDGES[index % AD_NUDGES.length];
  return (
    <div className="jp-ad-nudge">
      <div className="jp-ad-nudge-eyebrow"><Sparkles size={11} /> TypeMyCv · Resume Builder</div>
      <div className="jp-ad-nudge-title">{nudge.headline}</div>
      <div className="jp-ad-nudge-sub">{nudge.sub}</div>
      <button className="jp-ad-nudge-btn" onClick={onNavigate}>
        {nudge.cta} <ArrowRight size={14} />
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   SIDEBAR FILTERS
════════════════════════════════════════════════════════════════════ */
const FilterPanel = ({ filters, setFilters, onClear, isDrawer, onClose }) => (
  <div className={isDrawer ? undefined : 'jp-filter-panel'}>
    {isDrawer && (
      <button className="jp-drawer-close" onClick={onClose}><X size={15} /></button>
    )}
    <div className={isDrawer ? undefined : 'jp-filter-hd'} style={isDrawer ? { marginBottom: 20 } : {}}>
      <div className="jp-filter-hd-title" style={isDrawer ? { fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', fontWeight: 700 } : {}}>
        <Filter size={15} /> Filters
      </div>
      <button className="jp-clear-btn" onClick={onClear}>Clear all</button>
    </div>
    <div className={isDrawer ? undefined : 'jp-filter-body'} style={isDrawer ? { display: 'flex', flexDirection: 'column', gap: 20 } : {}}>
      <div className="jp-filter-group">
        <div className="jp-filter-label">Domain</div>
        <div className="jp-filter-chips">
          {Object.keys(CATEGORY_MAP).map(cat => (
            <button
              key={cat}
              className={`jp-chip${filters.category === cat ? ' active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, category: cat, page: 1 }))}
            >
              <span className="jp-chip-dot" />{cat}
            </button>
          ))}
        </div>
      </div>
      <div className="jp-filter-group">
        <div className="jp-filter-label">Job Type</div>
        <div className="jp-filter-chips">
          {Object.keys(CONTRACT_MAP).map(type => (
            <button
              key={type}
              className={`jp-chip${filters.contractType === type ? ' active' : ''}`}
              onClick={() => setFilters(f => ({ ...f, contractType: type, page: 1 }))}
            >
              <span className="jp-chip-dot" />{type}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   MAIN PORTAL COMPONENT
════════════════════════════════════════════════════════════════════ */
const JobPortal = () => {
  const navigate = useNavigate();

  const [keyword,  setKeyword]  = useState('');
  const [location, setLocation] = useState('');
  const [inputKw,  setInputKw]  = useState('');
  const [inputLoc, setInputLoc] = useState('');

  const [filters, setFilters] = useState({
    category:     'All Domains',
    contractType: 'All Types',
    page: 1,
  });

  const [jobs,    setJobs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Modal state
  const [modalJob,      setModalJob]      = useState(null);
  const [modalHookIdx,  setModalHookIdx]  = useState(0);
  const hookCounter = useRef(0);

  const jobsRef = useRef(null);
  const viewedCount = useRef(0);

  /* ── Fetch ── */
  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const category     = CATEGORY_MAP[filters.category]    || undefined;
      const contractType = CONTRACT_MAP[filters.contractType] || undefined;
      const { jobs: fetched, total: count } = await fetchAdzunaJobs({
        keyword, location, category, contractType, page: filters.page,
      });
      setJobs(fetched);
      setTotal(count);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [keyword, location, filters]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  /* ── Intersection observer ── */
  useEffect(() => {
    if (!jobsRef.current) return;
    const cards = jobsRef.current.querySelectorAll('.jp-job-card');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          viewedCount.current += 1;
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });
    cards.forEach(c => obs.observe(c));
    return () => obs.disconnect();
  }, [jobs]);

  /* ── Handlers ── */
  const handleSearch = () => {
    setKeyword(inputKw);
    setLocation(inputLoc);
    setFilters(f => ({ ...f, page: 1 }));
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleClearFilters = () => {
    setFilters({ category: 'All Domains', contractType: 'All Types', page: 1 });
  };

  const handleApplyClick = useCallback((job) => {
    setModalJob(job);
    setModalHookIdx(hookCounter.current % MODAL_HOOKS.length);
    hookCounter.current += 1;
  }, []);

  const handleModalClose = useCallback(() => {
    if (modalJob) {
      window.open(modalJob.url, '_blank', 'noopener,noreferrer');
    }
    setModalJob(null);
  }, [modalJob]);

  const handleBuildResume = useCallback(() => {
    setModalJob(null);
    navigate('/builder');
  }, [navigate]);

  const totalPages = Math.max(1, Math.ceil(total / 10));

  const renderItems = () => {
    const items = [];
    let nudgeIdx = 0;
    jobs.forEach((job, i) => {
      items.push(
        <JobCard
          key={job.id}
          job={job}
          style={{ animationDelay: `${i * 0.06}s` }}
          onApplyClick={handleApplyClick}
        />
      );
      if ((i + 1) % 5 === 0) {
        items.push(
          <AdNudge
            key={`nudge-${nudgeIdx}`}
            index={nudgeIdx}
            onNavigate={() => navigate('/builder')}
          />
        );
        nudgeIdx++;
      }
    });
    return items;
  };

  return (
    <div className="jp-root">

      {/* ── Apply Modal ── */}
      {modalJob && (
        <ApplyModal
          job={modalJob}
          onClose={handleModalClose}
          onBuildResume={handleBuildResume}
          hookIndex={modalHookIdx}
        />
      )}

      {/* ── Mobile filter drawer ── */}
      {drawerOpen && (
        <div className="jp-filter-drawer-backdrop" onMouseDown={() => setDrawerOpen(false)}>
          <div className="jp-filter-drawer" onMouseDown={e => e.stopPropagation()}>
            <FilterPanel
              filters={filters} setFilters={setFilters}
              onClear={handleClearFilters}
              isDrawer onClose={() => setDrawerOpen(false)}
            />
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
          NAVBAR — fixed, always visible
      ════════════════════════════════════ */}
      <nav className="jp-nav">
        <div className="jp-nav-brand" onClick={() => navigate('/')}>
          <img
            src="/typemycv.png"
            alt="TypeMyCv"
            className="jp-nav-logo-img"
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
          <span className="jp-nav-logo-text">
            TypeMyCv<span className="jp-nav-logo-dot" />
          </span>
          <span className="jp-nav-pill">JOBS</span>
        </div>

        <div className="jp-nav-right">
          <button className="jp-btn jp-btn-ghost" onClick={() => navigate('/')}>
            ← <span className="jp-btn-home-label">Home</span>
          </button>
          <button className="jp-btn jp-btn-primary" onClick={() => navigate('/builder')}>
            Build My CV <ArrowRight size={14} />
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="jp-hero">
        <div className="jp-hero-bg" />
        <div className="jp-hero-eyebrow">
          <TrendingUp size={13} /> Live Job Listings · Auto-updated Daily
        </div>
        <h1 className="jp-hero-h1">
          Find Your <em>Next Role.</em><br />
          Land It With the Right Resume.
        </h1>
        <p className="jp-hero-sub">
          Browse live openings across top companies. When you find the one —
          build an ATS-ready resume that actually gets through the filters.
        </p>

        <div className="jp-search-bar">
          <Search size={18} className="jp-search-icon" />
          <input
            className="jp-search-input"
            placeholder="Job title, company, or skill…"
            value={inputKw}
            onChange={e => setInputKw(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="jp-search-divider" />
          <div className="jp-search-location">
            <MapPin size={15} />
            <input
              placeholder="Location (e.g. Bengaluru)"
              value={inputLoc}
              onChange={e => setInputLoc(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button className="jp-search-btn" onClick={handleSearch}>
            <Search size={15} /> Search Jobs
          </button>
        </div>

        <div className="jp-stats-strip">
          <div className="jp-stat-item">
            <div className="jp-stat-val">{total > 0 ? total.toLocaleString() : '10K+'}</div>
            <div className="jp-stat-lbl">Live Openings</div>
          </div>
          <div className="jp-stat-sep" />
          <div className="jp-stat-item">
            <div className="jp-stat-val">500+</div>
            <div className="jp-stat-lbl">Companies</div>
          </div>
          <div className="jp-stat-sep" />
          <div className="jp-stat-item">
            <div className="jp-stat-val">Daily</div>
            <div className="jp-stat-lbl">Updated</div>
          </div>
          <div className="jp-stat-sep" />
          <div className="jp-stat-item">
            <div className="jp-stat-val">98%</div>
            <div className="jp-stat-lbl">ATS Pass Rate</div>
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="jp-main">
        <aside className="jp-sidebar">
          <FilterPanel
            filters={filters} setFilters={setFilters}
            onClear={handleClearFilters}
          />
        </aside>

        <div className="jp-content">
          <div className="jp-content-hd">
            <div className="jp-results-count">
              {loading ? 'Searching…' : (
                <>
                  <span>{total > 0 ? total.toLocaleString() : jobs.length}</span> jobs found
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="jp-mobile-filter-btn" onClick={() => setDrawerOpen(true)}>
                <SlidersHorizontal size={15} /> Filters
              </button>
            </div>
          </div>

          {loading && (
            <div className="jp-jobs-list">
              {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {!loading && error && (
            <div className="jp-empty">
              <div className="jp-empty-icon"><AlertCircle size={26} /></div>
              <div className="jp-empty-title">Couldn't load jobs</div>
              <div className="jp-empty-sub">{error}<br />
                <button className="jp-clear-btn" style={{ marginTop: 12 }} onClick={loadJobs}>Try again</button>
              </div>
            </div>
          )}

          {!loading && !error && jobs.length === 0 && (
            <div className="jp-empty">
              <div className="jp-empty-icon"><Briefcase size={26} /></div>
              <div className="jp-empty-title">No roles found</div>
              <div className="jp-empty-sub">Try a different keyword, location, or clear the filters.</div>
            </div>
          )}

          {!loading && !error && jobs.length > 0 && (
            <div className="jp-jobs-list" ref={jobsRef}>
              {renderItems()}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="jp-pagination">
              <button
                className="jp-pg-btn"
                disabled={filters.page <= 1}
                onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                aria-label="Previous page"
              >
                <ArrowLeft size={15} />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                const pg = filters.page <= 4 ? i + 1 : filters.page - 3 + i;
                if (pg < 1 || pg > totalPages) return null;
                return (
                  <button
                    key={pg}
                    className={`jp-pg-btn${filters.page === pg ? ' active' : ''}`}
                    onClick={() => setFilters(f => ({ ...f, page: pg }))}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                className="jp-pg-btn"
                disabled={filters.page >= totalPages}
                onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                aria-label="Next page"
              >
                <ArrowRight size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="jp-footer">
        <p className="jp-footer-copy">© 2026 TypeMyCv · Job listings powered by Adzuna</p>
        <p className="jp-footer-cta">
          Found your dream job?{' '}
          <a href="/builder" onClick={e => { e.preventDefault(); navigate('/builder'); }}>
            Build an ATS-ready resume in minutes →
          </a>
        </p>
      </footer>

    </div>
  );
};

export default JobPortal;